import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import {
    XMarkIcon,
    DocumentTextIcon,
    CheckCircleIcon,
    CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const LifeAtAhamModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fileName, setFileName] = useState('');

    // OTP States
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState('');
    const [otpVerified, setOtpVerified] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch
    } = useForm();

    // Helper to get correct API URL
    const getApiUrl = (path) => {
        const isLocal = window.location.hostname === 'localhost';
        return isLocal ? `http://localhost:8001${path}` : path;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                toast.error('File size should be less than 5MB');
                e.target.value = null;
                setFileName('');
                return;
            }
            setFileName(file.name);
        } else {
            setFileName('');
        }
    };

    const phoneValue = watch('mobile', '');

    // Handle OTP sending via Karix
    const handleSendOtp = async () => {
        if (!phoneValue) {
            toast.error('Please enter your phone number first');
            return;
        }
        if (phoneValue.length < 10) {
            toast.error('Please enter a valid 10-digit mobile number');
            return;
        }

        setIsSendingOtp(true);

        try {
            const response = await fetch(getApiUrl('/api/otp/send'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: phoneValue }),
            });
            const data = await response.json();
            if (data.success) {
                setOtpSent(true);
                toast.success('OTP sent successfully!');
            } else {
                toast.error(data.message || 'Failed to send OTP.');
            }
        } catch (error) {
            console.error('OTP Send error:', error);
            toast.error('Error sending OTP.');
        } finally {
            setIsSendingOtp(false);
        }
    };

    // Handle Verify OTP
    const handleVerifyOtp = async () => {
        if (!otp || otp.length < 6) {
            toast.error('Please enter the 6-digit OTP');
            return;
        }

        const verifyToast = toast.loading('Verifying OTP...');

        try {
            const response = await fetch(getApiUrl('/api/otp/verify'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: phoneValue, otp }),
            });
            const data = await response.json();
            if (data.success) {
                setOtpVerified(true);
                toast.success('OTP verified successfully!', { id: verifyToast });
            } else {
                toast.error(data.message || 'Invalid or expired OTP', { id: verifyToast });
            }
        } catch (error) {
            console.error('OTP Verify error:', error);
            toast.error('An error occurred during verification.', { id: verifyToast });
        }
    };

    const onSubmit = async (data) => {
        if (!otpVerified) {
            toast.error('Please verify your phone number with OTP first');
            return;
        }
        setIsSubmitting(true);
        const loadingToast = toast.loading('Submitting your application...');

        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('email', data.email);
        formData.append('phone', data.mobile);
        if (data.resume && data.resume[0]) {
            formData.append('resume', data.resume[0]);
        }

        try {
            const response = await axios.post(getApiUrl('/api/life-at-aham/apply'), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                toast.success('Application submitted successfully!', { id: loadingToast });
                navigate('/thank-you');
                onClose();
            } else {
                toast.error(response.data.message || 'Failed to submit application.', { id: loadingToast });
            }
        } catch (error) {
            console.error('Submission failed:', error);
            toast.error(error.response?.data?.message || 'Failed to submit application. Please try again.', { id: loadingToast });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        reset();
        setFileName('');
        setOtpSent(false);
        setOtp('');
        setOtpVerified(false);
        onClose();
    };

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };
        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
        }
        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, [isOpen]);

    const modalVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
        exit: { opacity: 0 }
    };

    const contentVariants = {
        hidden: { opacity: 0, scale: 0.9, y: 50 },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 25
            }
        },
        exit: {
            opacity: 0,
            scale: 0.9,
            y: 50,
            transition: { duration: 0.2 }
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fixed inset-0 z-[10001] overflow-y-auto"
            >
                {/* Backdrop */}
                <div className="flex min-h-screen items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={handleClose}
                    />

                    {/* Modal Content */}
                    <motion.div
                        variants={contentVariants}
                        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white p-6 relative overflow-hidden">
                            <button
                                onClick={handleClose}
                                className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>

                            <div className="flex items-center space-x-3">
                                <div className="bg-white/20 p-2 rounded-xl">
                                    <DocumentTextIcon className="h-6 w-6" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold">Join Our Team</h3>
                                    <p className="text-sm text-white/80">Start your journey with Aham</p>
                                </div>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="p-6">
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Full Name *
                                    </label>
                                    <input
                                        {...register('name', {
                                            required: 'Name is required',
                                            minLength: { value: 2, message: 'Name must be at least 2 characters' }
                                        })}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Enter your full name"
                                    />
                                    {errors.name && (
                                        <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                                    )}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Email Address *
                                    </label>
                                    <input
                                        {...register('email', {
                                            required: 'Email is required',
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Invalid email address"
                                            }
                                        })}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Enter your email"
                                        type="email"
                                    />
                                    {errors.email && (
                                        <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                                    )}
                                </div>

                                {/* Mobile */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Phone Number *
                                    </label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            {...register('mobile', {
                                                required: 'Phone number is required',
                                                pattern: {
                                                    value: /^[0-9]{10}$/,
                                                    message: "Please enter a valid 10-digit mobile number"
                                                },
                                                onChange: (e) => {
                                                    if (otpSent || otpVerified) {
                                                        setOtpSent(false);
                                                        setOtpVerified(false);
                                                        setOtp('');
                                                    }
                                                }
                                            })}
                                            className={`w-full sm:flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.mobile ? 'border-red-500' : 'border-gray-300'} ${otpVerified ? 'border-green-500 bg-green-50' : ''}`}
                                            placeholder="Enter 10-digit mobile"
                                            maxLength={10}
                                            disabled={otpVerified}
                                        />
                                        {!otpVerified && (
                                            <button
                                                type="button"
                                                onClick={handleSendOtp}
                                                disabled={isSendingOtp || !phoneValue || phoneValue.length < 10}
                                                className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${isSendingOtp || !phoneValue || phoneValue.length < 10
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-primary-600 text-white hover:bg-primary-700 shadow-md'
                                                    }`}
                                            >
                                                {isSendingOtp ? 'Sending...' : otpSent ? 'Resend' : 'Send OTP'}
                                            </button>
                                        )}
                                        {otpVerified && (
                                            <div className="flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium">
                                                <CheckCircleIcon className="h-5 w-5 mr-1" />
                                                Verified
                                            </div>
                                        )}
                                    </div>
                                    {errors.mobile && (
                                        <p className="text-red-500 text-xs mt-1">{errors.mobile.message}</p>
                                    )}

                                    {/* OTP Verification Section */}
                                    {otpSent && !otpVerified && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="mt-3 bg-blue-50 p-4 rounded-lg border border-blue-200"
                                        >
                                            <label className="block text-sm font-medium text-blue-800 mb-2 text-center">
                                                Enter 6-digit OTP
                                            </label>
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <input
                                                    type="text"
                                                    value={otp}
                                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                    placeholder="000000"
                                                    maxLength={6}
                                                    className="w-full sm:flex-1 p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-600 transition text-center text-lg tracking-widest"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleVerifyOtp}
                                                    disabled={otp.length < 6}
                                                    className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium transition-all ${otp.length < 6
                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        : 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                                                        }`}
                                                >
                                                    Verify
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </div>

                                {/* Resume Upload */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Upload Resume (PDF/DOC)
                                    </label>
                                    <div className={`relative border-2 border-dashed rounded-lg p-4 transition-colors ${fileName ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-primary-500'}`}>
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx"
                                            {...register('resume', {
                                                onChange: handleFileChange
                                            })}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <div className="flex flex-col items-center justify-center text-center">
                                            {fileName ? (
                                                <>
                                                    <CheckCircleIcon className="h-8 w-8 text-green-500 mb-2" />
                                                    <span className="text-sm font-medium text-green-700 truncate max-w-full px-2">
                                                        {fileName}
                                                    </span>
                                                    <span className="text-xs text-green-600 mt-1">Click to change</span>
                                                </>
                                            ) : (
                                                <>
                                                    <CloudArrowUpIcon className="h-8 w-8 text-gray-400 mb-2" />
                                                    <span className="text-sm font-medium text-gray-600">
                                                        Click to upload valid file
                                                    </span>
                                                    <span className="text-xs text-gray-500 mt-1">
                                                        PDF, DOC, DOCX (Max 2MB)
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || !otpVerified}
                                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${isSubmitting || !otpVerified
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center justify-center space-x-2">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                                                <span>Submitting...</span>
                                            </div>
                                        ) : (
                                            'Submit Application'
                                        )}
                                    </button>
                                    {!otpVerified && (
                                        <p className="text-[10px] text-gray-500 text-center mt-2">
                                            Please verify your phone number with OTP to submit
                                        </p>
                                    )}
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default LifeAtAhamModal;
